import {
  PaymentElement,
  useStripe,
  useElements,
  Elements
} from '@stripe/react-stripe-js'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

// Separate component that uses Stripe hooks - must be inside Elements provider
export default function PaymentForm({ customerInfo, onCustomerInfoChange, onConfirmBooking, businessId, workerId, paymentCompleted, onPaymentCompleted, serviceId, showPaymentForm }) {
  const navigate = useNavigate();
  const stripe = useStripe()
  const elements = useElements()
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentSucceeded, setPaymentSucceeded] = useState(paymentCompleted || false);
  const [attemptingBooking, setAttemptingBooking] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    name: '',
    email: '',
    contact_number: ''
  });

  const validateName = (name) => {
    if (!name || name.trim().length === 0) {
      return 'Name is required';
    }
    if (name.trim().length < 2) {
      return 'Name must be at least 2 characters';
    }
    if (!/^[a-zA-Z\s'-]+$/.test(name)) {
      return 'Name can only contain letters, spaces, hyphens, and apostrophes';
    }
    return '';
  };

  const validateEmail = (email) => {
    if (!email || email.trim().length === 0) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validatePhone = (phone) => {
    if (!phone || phone.trim().length === 0) {
      return 'Phone number is required';
    }
    // Remove spaces, hyphens, and parentheses for validation
    const cleanPhone = phone.replace(/[\s\-()]/g, '');
    // Check if it contains only digits and optional + at start
    if (!/^\+?\d{10,15}$/.test(cleanPhone)) {
      return 'Please enter a valid phone number (10-15 digits)';
    }
    return '';
  };

  const handleInputChange = (field, value) => {
    onCustomerInfoChange(field, value);
    
    // Clear validation error for this field when user starts typing
    setValidationErrors(prev => ({
      ...prev,
      [field]: ''
    }));
  };

  const handleInputBlur = (field, value) => {
    let error = '';
    switch (field) {
      case 'name':
        error = validateName(value);
        break;
      case 'email':
        error = validateEmail(value);
        break;
      case 'contact_number':
        error = validatePhone(value);
        break;
      default:
        break;
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const validateAllFields = () => {
    const nameError = validateName(customerInfo.name);
    const emailError = validateEmail(customerInfo.email);
    const phoneError = validatePhone(customerInfo.contact_number);
    
    setValidationErrors({
      name: nameError,
      email: emailError,
      contact_number: phoneError
    });
    
    return !nameError && !emailError && !phoneError;
  };

  const handleCashPayment = async () => {
    if (!validateAllFields()) {
      setMessage('Please fill in all required fields correctly')
      return
    }

    setIsLoading(true)
    setMessage(null)

    try {
      setMessage('Creating appointment...')
      const createdAppointment = await onConfirmBooking()
      
      if (createdAppointment && createdAppointment.id) {
        // Navigate to feedback page with appointment details
        navigate(`/feedback?businessId=${businessId}&appointmentId=${createdAppointment.id}&serviceId=${serviceId}`)
      } else {
        setMessage('There was an issue creating the appointment. Please try again.')
      }
    } catch (error) {
      console.error('Error creating appointment:', error)
      setMessage('There was an issue creating the appointment. Please try again.')
    }
    
    setIsLoading(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    // Validate customer info before proceeding
    if (!validateAllFields()) {
      setMessage('Please fill in all required fields correctly')
      return
    }

    // If payment already succeeded but booking failed, just retry booking
    if (paymentSucceeded && !attemptingBooking) {
      setAttemptingBooking(true);
      setMessage('Retrying appointment creation...');
      
      try {
        const createdAppointment = await onConfirmBooking();
        
        if (createdAppointment && createdAppointment.id) {
          navigate(`/feedback?businessId=${businessId}&appointmentId=${createdAppointment.id}&serviceId=${serviceId}`);
        } else {
          setMessage('Payment successful, but there was an issue creating the appointment. Please try again or contact support.');
        }
      } catch (error) {
        console.error('Error retrying appointment creation:', error);
        setMessage('Payment successful, but there was an issue creating the appointment. Please try again or contact support.');
      }
      
      setAttemptingBooking(false);
      return;
    }

    // Prevent multiple payment attempts if one is already in progress
    if (isLoading || paymentSucceeded) {
      return;
    }

    setIsLoading(true)
    setMessage(null) // Clear any previous messages

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: 'https://booking.schedlo.org/booking'
      },
      redirect: 'if_required'
    })

    if (error) {
      // Payment failed - show error message and don't call onConfirmBooking
      if (error.type === 'card_error' || error.type === 'validation_error') {
        setMessage(error.message)
      } else {
        setMessage('An unexpected error occurred.')
      }
      setIsLoading(false)
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Payment succeeded - mark as successful and attempt appointment creation
      setPaymentSucceeded(true);
      if (onPaymentCompleted) {
        onPaymentCompleted(true);
      }
      setAttemptingBooking(true);
      
      try {
        setMessage('Payment successful! Creating appointment...')
        const createdAppointment = await onConfirmBooking()
        
        if (createdAppointment && createdAppointment.id) {
          // Navigate to feedback page with appointment details
          navigate(`/feedback?businessId=${businessId}&appointmentId=${createdAppointment.id}&serviceId=${serviceId}`)
        } else {
          setMessage('Payment successful, but there was an issue creating the appointment. Please try again or contact support.')
        }
      } catch (error) {
        console.error('Error after payment:', error)
        setMessage('Payment successful, but there was an issue creating the appointment. Please try again or contact support.')
      }
      
      setAttemptingBooking(false);
      setIsLoading(false)
    } else {
      // Payment in unexpected state
      setMessage('Payment status unclear. Please contact support.')
      setIsLoading(false)
    }
  }

  const paymentElementOptions = {
    layout: "accordion"
  }

  return (
    <div className="customer-info">
      <h3>Your Information</h3>
      <div className="form-group">
        <input 
          type="text" 
          placeholder="Full Name *" 
          className={`form-input ${validationErrors.name ? 'input-error' : ''}`}
          value={customerInfo.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          onBlur={(e) => handleInputBlur('name', e.target.value)}
          required
        />
        {validationErrors.name && (
          <span className="error-message">{validationErrors.name}</span>
        )}
      </div>
      <div className="form-group">
        <input 
          type="email" 
          placeholder="Email Address *" 
          className={`form-input ${validationErrors.email ? 'input-error' : ''}`}
          value={customerInfo.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          onBlur={(e) => handleInputBlur('email', e.target.value)}
          required
        />
        {validationErrors.email && (
          <span className="error-message">{validationErrors.email}</span>
        )}
      </div>
      <div className="form-group">
        <input 
          type="tel" 
          placeholder="Phone Number *" 
          className={`form-input ${validationErrors.contact_number ? 'input-error' : ''}`}
          value={customerInfo.contact_number}
          onChange={(e) => handleInputChange('contact_number', e.target.value)}
          onBlur={(e) => handleInputBlur('contact_number', e.target.value)}
          required
        />
        {validationErrors.contact_number && (
          <span className="error-message">{validationErrors.contact_number}</span>
        )}
      </div>
      {showPaymentForm ? (
      <form onSubmit={handleSubmit}>
        {!paymentSucceeded && (
          <PaymentElement id="payment-element" options={paymentElementOptions} />
        )}
        <div className="confirm-section mt-3">
          <button 
            className="confirm-btn" 
            type="submit" 
            disabled={
              isLoading || 
              attemptingBooking || 
              !stripe || 
              !elements || 
              (!paymentSucceeded && (!customerInfo.name || !customerInfo.email || !customerInfo.contact_number || validationErrors.name || validationErrors.email || validationErrors.contact_number))
            }
          >
            {isLoading ? 'Processing Payment...' : 
             attemptingBooking ? 'Creating Appointment...' :
             paymentSucceeded ? 'Retry Appointment Creation' :
             `Pay £${customerInfo.price || '0.00'}`}
          </button>
          {paymentSucceeded && (
            <p className="payment-success-note">
              ✓ Payment completed successfully. {attemptingBooking ? 'Creating appointment...' : 'Click above to retry appointment creation if needed.'}
            </p>
          )}
        </div>
        {message && <div className="payment-message">{message}</div>}
      </form>        
      ) : (
        null
      )}

    </div>
  )
}